import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { StyledButton } from '../../styles/StyledButtons';
import StyledSection from '../../styles/StyledSection';
import { StyledHeading } from '../../styles/styles';
import { FundDetailTextField } from './FundDetailFormFields';
import FundDetailsSection from './FundDetailsSection';
import { FundDetailsFormData } from './fundDetailsFormTypes';

export type { FundDetailsFormData } from './fundDetailsFormTypes';

export interface FundDetailsFormProps {
  formData: FundDetailsFormData;
  fieldErrors?: Record<string, string>;
  isSubmitting: boolean;
  onInputChange: (
    field: keyof FundDetailsFormData,
    value: string | number | string[] | boolean,
  ) => void;
  onSectorChange: (sectorValue: FundDetailsFormData['sector'][number]) => void;
  onSubmit: () => void;
}

const FundDetailsForm: React.FC<FundDetailsFormProps> = ({
  formData,
  fieldErrors,
  isSubmitting,
  onInputChange,
  onSectorChange,
  onSubmit,
}) => {
  const [dirty, setDirty] = useState(false);
  const wasSubmitting = useRef(isSubmitting);

  useEffect(() => {
    if (wasSubmitting.current && !isSubmitting) {
      setDirty(false);
    }
    wasSubmitting.current = isSubmitting;
  }, [isSubmitting]);

  const handleInputChange = (
    field: keyof FundDetailsFormData,
    value: string | number | string[] | boolean,
  ) => {
    setDirty(true);
    onInputChange(field, value);
  };

  const handleSectorChange = (sectorValue: FundDetailsFormData['sector'][number]) => {
    setDirty(true);
    onSectorChange(sectorValue);
  };

  return (
    <StyledSection tone="tone1" noTopPadding>
      <StyledHeading fullTopSpacing>Fund Details</StyledHeading>

      <StyledHeading headerVariant="subheading">Overview</StyledHeading>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
        gap={2}
        sx={{ mt: 1 }}
      >
        <FundDetailTextField
          label="Fund Name"
          name="name"
          value={formData.name ?? ''}
          onChange={(value) => handleInputChange('name', value)}
          error={fieldErrors?.name}
        />
        <Box sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}>
          <FundDetailTextField
            label="Summary"
            name="summaryTitle"
            value={formData.summaryTitle ?? ''}
            onChange={(value) => handleInputChange('summaryTitle', value)}
            error={fieldErrors?.summaryTitle}
          />
        </Box>
        <FundDetailTextField
          label="Fund Size"
          name="fundSize"
          value={formData.fundSize ?? ''}
          onChange={(value) => handleInputChange('fundSize', value)}
          error={fieldErrors?.fundSize}
          number
          startAdornment="$"
        />
        <FundDetailTextField
          label="Fund Duration"
          name="fundDuration"
          value={formData.fundDuration ?? ''}
          onChange={(value) => handleInputChange('fundDuration', value)}
          error={fieldErrors?.fundDuration}
          number
          endAdornment="months"
        />
        <FundDetailTextField
          label="Return on Investment"
          name="returnOnInvestment"
          value={formData.returnOnInvestment ?? ''}
          onChange={(value) => handleInputChange('returnOnInvestment', value)}
          error={fieldErrors?.returnOnInvestment}
          number
          endAdornment="%"
        />
      </Box>

      <FundDetailsSection
        formData={formData}
        fieldErrors={fieldErrors}
        onInputChange={handleInputChange}
        onSectorChange={handleSectorChange}
      />

      <Box mt="9px">
        <StyledButton
          disabled={isSubmitting || !dirty}
          buttonVariant={dirty ? 'default' : 'inactiveOrCancel'}
          onClick={onSubmit}
        >
          Save
        </StyledButton>
      </Box>
    </StyledSection>
  );
};

export default FundDetailsForm;
